import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { User, Mail, Lock, Eye, EyeOff, Loader, AlertTriangle, UserPlus, ChevronDown } from 'lucide-react';
import { getCountries, getCountryCallingCode, isValidPhoneNumber } from 'react-phone-number-input';
import { getExampleNumber, AsYouType } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import en from 'react-phone-number-input/locale/en.json';
import './custom-phone-input.css';

const toFlagEmoji = (code) =>
    String.fromCodePoint(...code.toUpperCase().split('').map(c => 0x1F1E6 - 65 + c.charCodeAt(0)));

const getPhoneInfo = (country) => {
    try {
        const example = getExampleNumber(country, examples);
        if (!example) return { hint: '', digitCount: 15 };
        const intl = example.formatInternational();
        return {
            hint: intl,
            digitCount: example.nationalNumber.length,
        };
    } catch {
        return { hint: '', digitCount: 15 };
    }
};

const ALL_COUNTRIES = getCountries();

const Signup = () => {
    const [formData, setFormData] = useState({
        name: '',
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        invitationCode: '',
        mobile: '',
    });
    // nationalDigits holds only the digits the user typed (no country code)
    const [nationalDigits, setNationalDigits] = useState('');
    const [phoneCountry, setPhoneCountry] = useState('US');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { signup } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useState(() => {
        const params = new URLSearchParams(location.search);
        const refCode = params.get('ref');
        if (refCode) setFormData(prev => ({ ...prev, invitationCode: refCode }));
    }, [location.search]);

    const phoneInfo = getPhoneInfo(phoneCountry);
    const callingCode = (() => {
        try { return getCountryCallingCode(phoneCountry); } catch { return ''; }
    })();

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    // Strip digits from whatever the user typed (handles formatted text + backspace correctly)
    const handlePhoneInput = (e) => {
        const digits = e.target.value.replace(/\D/g, '').slice(0, phoneInfo.digitCount);
        setNationalDigits(digits);
        setFormData(prev => ({ ...prev, mobile: digits ? `+${callingCode}${digits}` : '' }));
    };

    // Format raw digits into national format as the user types e.g. (234) 908-5689
    const formattedPhone = (() => {
        if (!nationalDigits) return '';
        try {
            return new AsYouType(phoneCountry).input(nationalDigits);
        } catch {
            return nationalDigits;
        }
    })();

    const handleCountryChange = (e) => {
        const newCountry = e.target.value;
        setPhoneCountry(newCountry);
        setNationalDigits('');
        setFormData(prev => ({ ...prev, mobile: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.mobile || !isValidPhoneNumber(formData.mobile)) {
            setError('Please enter a valid phone number with country code.');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            await signup(formData.name, formData.username, formData.email, formData.password, formData.invitationCode, formData.mobile);
            navigate('/');
        } catch (err) {
            setError(err.message || 'An unknown error occurred during signup.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>Create Account | RatingApp — Start Earning USDT</title>
                <meta name="description" content="Join RatingApp for free and start earning USDT by rating top global apps. Sign up in minutes and claim your welcome bonus." />
            </Helmet>
            <div className="flex items-center justify-center min-h-screen from-gray-900 via-blue-900/30 to-gray-900 text-white p-4">
                <motion.div
                    className="w-full max-w-md"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <div className="bg-gray-800/30 backdrop-blur-xl border border-white/10 shadow-2xl shadow-cyan-500/10 rounded-3xl p-8">
                        <div className="text-center mb-8">
                            <h1 className="text-4xl font-extrabold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-cyan-400 to-blue-500">
                                Create Account
                            </h1>
                            <p className="text-slate-400 mt-2">Join our community and start earning.</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <InputField icon={<User size={20} />} name="name" type="text" placeholder="Full Name" value={formData.name} onChange={handleChange} required />
                            <InputField icon={<UserPlus size={20} />} name="username" type="text" placeholder="Username" value={formData.username} onChange={handleChange} required />
                            <InputField icon={<Mail size={20} />} name="email" type="email" placeholder="Email Address" value={formData.email} onChange={handleChange} required />

                            {/* Phone field */}
                            <div>
                                <div className="phone-field">
                                    {/* Country selector */}
                                    <div className="phone-field__country">
                                        <select value={phoneCountry} onChange={handleCountryChange} aria-label="Country">
                                            {ALL_COUNTRIES.map(c => (
                                                <option key={c} value={c}>
                                                    {en[c]} (+{getCountryCallingCode(c)})
                                                </option>
                                            ))}
                                        </select>
                                        <span className="phone-field__flag">{toFlagEmoji(phoneCountry)}</span>
                                        <span className="phone-field__code">+{callingCode}</span>
                                        <ChevronDown size={13} className="phone-field__chevron" />
                                    </div>

                                    <div className="phone-field__divider" />

                                    {/* Displays formatted number; digits are extracted on every change */}
                                    <input
                                        type="tel"
                                        value={formattedPhone}
                                        onChange={handlePhoneInput}
                                        placeholder="Phone Number"
                                        className="phone-field__input"
                                    />
                                </div>

                                {/* Format + digit count hint */}
                                {phoneInfo.hint && (
                                    <p className="flex items-center gap-1.5 text-xs text-slate-500 mt-1.5 pl-1">
                                        <span className="text-slate-600">Format:</span>
                                        <span className="font-mono tracking-wide">{phoneInfo.hint}</span>
                                        <span className="ml-auto tabular-nums">
                                            <span className={nationalDigits.length === phoneInfo.digitCount ? 'text-cyan-400' : 'text-slate-600'}>
                                                {nationalDigits.length}
                                            </span>
                                            <span className="text-slate-600">/{phoneInfo.digitCount}</span>
                                        </span>
                                    </p>
                                )}
                            </div>

                            <PasswordField icon={<Lock size={20} />} name="password" placeholder="Password" value={formData.password} onChange={handleChange} show={showPassword} onToggle={() => setShowPassword(!showPassword)} required />
                            <PasswordField icon={<Lock size={20} />} name="confirmPassword" placeholder="Confirm Password" value={formData.confirmPassword} onChange={handleChange} show={showConfirmPassword} onToggle={() => setShowConfirmPassword(!showConfirmPassword)} required />
                            <InputField icon={<UserPlus size={20} />} name="invitationCode" type="text" placeholder="Invitation Code (Optional)" value={formData.invitationCode} onChange={handleChange} />

                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        className="flex items-center gap-2 text-sm text-red-400 bg-red-500/10 p-3 rounded-lg"
                                    >
                                        <AlertTriangle size={18} />
                                        <span>{error}</span>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <motion.button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold text-lg py-3 rounded-xl shadow-lg shadow-cyan-500/20 border-t border-cyan-400/50 transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                            >
                                {loading ? <><Loader className="animate-spin" /> Creating Account...</> : 'Sign Up'}
                            </motion.button>

                            <div className="text-sm text-center text-slate-400">
                                <p>Already have an account? <Link to="/login" className="font-semibold text-cyan-400 hover:underline">Sign In</Link></p>
                            </div>
                        </form>
                    </div>
                </motion.div>
            </div>
        </>
    );
};

const InputField = ({ icon, ...props }) => (
    <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span>
        <input {...props} className="w-full bg-gray-900/50 border border-transparent hover:border-white/20 focus:border-cyan-500 rounded-xl py-3 pl-12 pr-4 text-white placeholder-slate-400 transition-all duration-300 focus:ring-2 focus:ring-cyan-500/50" />
    </div>
);

const PasswordField = ({ icon, show, onToggle, ...props }) => (
    <div className="relative">
        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">{icon}</span>
        <input {...props} type={show ? 'text' : 'password'} className="w-full bg-gray-900/50 border border-transparent hover:border-white/20 focus:border-cyan-500 rounded-xl py-3 pl-12 pr-12 text-white placeholder-slate-400 transition-all duration-300 focus:ring-2 focus:ring-cyan-500/50" />
        <button type="button" onClick={onToggle} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white">
            {show ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
    </div>
);

export default Signup;
